
// src/components/TemplateList.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './TemplateList.css';
import config from '../config';

const TemplateList = ({ onSelectTemplate, onEditTemplate, onCreateNew }) => {
  const costPerPdf = parseFloat(config.COST_PER_PDF)
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const { user, isAuthenticated, logout } = useAuth();
  const [limitError, setLimitError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false); // For modal popup
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true); // Add this

  // // Add error display
  // {
  //   limitError && (
  //     <div className="limit-error">
  //       <span>⚠️</span>
  //       {limitError}
  //       <button onClick={() => window.location.href = '/settings/billing'}>Upgrade Now</button>
  //     </div>
  //   )
  // }

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await fetch(`${config.API_BASE_URL}/api/analytics/`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Modify the create template function
  const handleCreateTemplate = async () => {
    const templatesUsed = analyticsData.profile.free_templates_used;
    const templatesLimit = analyticsData.profile.free_template_limit;
    const accBalance = analyticsData.profile.balance;
    const pdfRate = analyticsData.profile.pdf_generation_rate;
    if (analyticsData?.profile?.plan === 'free') {
      if (templatesUsed >= templatesLimit) {
        const errorMsg = `You have reached your free plan limit of ${analyticsData.profile.free_template_limit} templates. Please upgrade to continue.`;
        setLimitError(errorMsg);
        setShowUpgradeModal(true);
        return;
      }
    }
    else if (analyticsData?.profile?.plan === 'usage') {
      if (templatesUsed >= templatesLimit) {
        if (accBalance <= pdfRate) {
          const errorMsg = `⚠️ Low balance! Please recharge to create templates. Your balance is ${accBalance}.`;
          setLimitError(errorMsg);
          setShowUpgradeModal(true);
          return;
        }
      }
    }
    onCreateNew();
  };

  // Function to close modal
  const closeModal = () => {
    setShowUpgradeModal(false);
    setLimitError('');
  };

  // Function to navigate to upgrade page
  const handleUpgrade = () => {
    // Navigate to analytics/billing page
    window.location.href = '/analytics';
  };


  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const csrfToken = getCookie('csrftoken');

      const response = await fetch(`${config.API_BASE_URL}/api/form-templates/`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAnalytics();
      fetchTemplates();
    }
  }, [isAuthenticated]);

  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete "${templateName}"?`)) {
      return;
    }

    try {
      const csrfToken = getCookie('csrftoken');

      if (!csrfToken) {
        console.error('No CSRF token found');
        alert('Security token not found. Please refresh the page and try again.');
        return;
      }

      const response = await fetch(`${config.API_BASE_URL}/api/form-templates/${templateId}/`, {
        method: 'DELETE',
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 403) {
          await refreshCSRFToken();
          const newToken = getCookie('csrftoken');
          if (newToken) {
            const retryResponse = await fetch(`${config.API_BASE_URL}/api/form-templates/${templateId}/`, {
              method: 'DELETE',
              headers: {
                'X-CSRFToken': newToken,
                'Content-Type': 'application/json',
              },
              credentials: 'include',
            });

            if (retryResponse.ok) {
              await fetchTemplates();
              return;
            }
          }
          throw new Error('Permission denied. Please login again.');
        } else if (response.status === 401) {
          throw new Error('You need to be logged in to delete templates');
        } else if (response.status === 404) {
          throw new Error('Template not found');
        } else {
          throw new Error(`Server error: ${response.status}`);
        }
      }

      await fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(`Failed to delete template: ${error.message}`);
    }
  };

  const refreshCSRFToken = async () => {
    try {
      await fetch(`${config.API_BASE_URL}/api/auth/csrf/`, {
        method: 'GET',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error refreshing CSRF token:', error);
    }
  };

  // Filter templates based on search and filter type
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' ||
      (filterType === 'recent' && new Date(template.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
    return matchesSearch && matchesFilter;
  });

  const getTotalFields = (template) => {
    return template.sections?.reduce((total, section) =>
      total + (section.fields?.length || 0), 0) || 0;
  };

  const getTotalResponses = (template) => {
    return template.responses?.length || 0;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your templates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-icon">⚠️</div>
        <h3>Something went wrong</h3>
        <p>{error}</p>
        <button onClick={fetchTemplates} className="retry-btn">Try Again</button>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Upgrade Modal Popup */}
      {showUpgradeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <span className="modal-icon">⚠️</span>
              <h2>Template Limit Reached</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <p>{limitError}</p>
              <div className="modal-options">
                <div className="option-card">
                  <h3>📊 Free Plan</h3>
                  <p>Your current plan</p>
                  <ul>
                    <li>✓ {analyticsData?.profile?.free_template_limit || 2} Templates (max)</li>
                    <li>✓ {analyticsData?.profile?.free_pdf_limit || 5} PDFs lifetime</li>
                    <li>✓ Basic form fields</li>
                  </ul>
                </div>
                <div className="option-card recommended">
                  <div className="recommended-badge">Recommended</div>
                  <h3>🚀 Usage Plan</h3>
                  <p>Unlimited templates and pay per PDF</p>
                  <ul>
                    <li>✓ Unlimited templates</li>
                    <li>✓ ${analyticsData?.profile?.pdf_generation_rate || costPerPdf} per PDF generation</li>
                    <li>✓ Image upload support</li>
                    <li>✓ Priority support</li>
                  </ul>
                  <button onClick={handleUpgrade} className="upgrade-modal-btn">
                    Upgrade Now →
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeModal} className="cancel-btn">Cancel</button>
              <button onClick={handleUpgrade} className="upgrade-footer-btn">View Plans</button>
            </div>
          </div>
        </div>
      )}
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Welcome Section */}
        <div className="welcome-section">
          <div className="welcome-content">
            <h1>Welcome back, {user?.username}!</h1>
            <p>Manage your forms, track responses, and create new templates</p>
          </div>
          <button onClick={handleCreateTemplate} className="create-template-btn">
            <span className="btn-icon">+</span>
            Create New Template
          </button>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <h3>{templates.length}</h3>
              <p>Total Templates</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-info">
              <h3>{templates.reduce((sum, t) => sum + getTotalFields(t), 0)}</h3>
              <p>Total Fields</p>
            </div>
          </div>
          {/* <div className="stat-card">
            <div className="stat-icon">📄</div>
            <div className="stat-info">
              <h3>{templates.reduce((sum, t) => sum + getTotalResponses(t), 0)}</h3>
              <p>Total Responses</p>
            </div>
          </div> */}
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-info">
              {analyticsLoading ? (
                <div className="loading-placeholder">Loading...</div>
              ) : (
                <h3>{analyticsData?.profile?.plan === 'free' ? 'Free Plan' : 'Usage Plan'}</h3>
              )}
              <p>Current Plan</p>
            </div>
          </div>
        </div>

        {/* Templates Section Header */}
        <div className="templates-header">
          <div className="header-left">
            <h2>My Templates</h2>
            <span className="template-badge">{filteredTemplates.length} templates</span>
          </div>
          <div className="header-right">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <select
              className="filter-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Templates</option>
              <option value="recent">Recent (30 days)</option>
            </select>
          </div>
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length === 0 ? (
          <div className="empty-templates">
            <div className="empty-icon">📋</div>
            <h3>No templates found</h3>
            <p>{searchTerm ? `No templates matching "${searchTerm}"` : "You haven't created any templates yet"}</p>
            {!searchTerm && (
              <button onClick={onCreateNew} className="empty-create-btn">
                Create Your First Template
              </button>
            )}
          </div>
        ) : (
          <div className="templates-grid">
            {filteredTemplates.map(template => (
              <div key={template.id} className="template-card">
                <div className="card-header">
                  <div className="template-icon">📄</div>
                  <div className="template-info">
                    <h3 className="template-name">{template.name}</h3>
                    <p className="template-date">
                      Created {new Date(template.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="card-stats">
                  <div className="stat-item">
                    <span className="stat-value">{template.sections?.length || 0}</span>
                    <span className="stat-label">Sections</span>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-item">
                    <span className="stat-value">{getTotalFields(template)}</span>
                    <span className="stat-label">Fields</span>
                  </div>
                  {/* <div className="stat-divider"></div>
                  <div className="stat-item">
                    <span className="stat-value">{getTotalResponses(template)}</span>
                    <span className="stat-label">Responses</span>
                  </div> */}
                </div>

                <div className="card-actions">
                  <button
                    onClick={() => onSelectTemplate(template)}
                    className="action-btn btn-fill"
                    title="Fill Form"
                  >
                    <span className="btn-icon">📝</span>
                    Fill
                  </button>
                  <button
                    onClick={() => onEditTemplate(template)}
                    className="action-btn btn-edit"
                    title="Edit Template"
                  >
                    <span className="btn-icon">✏️</span>
                    Edit
                  </button>
                  <button
                    onClick={() => deleteTemplate(template.id, template.name)}
                    className="action-btn btn-delete"
                    title="Delete Template"
                  >
                    <span className="btn-icon">🗑️</span>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default TemplateList;